import React, {useEffect, useState} from "react";
import {IconButton, MenuItem, Select, SelectChangeEvent, Stack, SxProps, Typography} from "@mui/material";
import dayjs, {Dayjs} from "dayjs";
import {DatePicker} from "@mui/x-date-pickers";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {Order} from "../../types/domain/order";

import type {MRT_Column} from "material-react-table";
import {TFunction} from "i18next";
export interface Props {
    date: dayjs.Dayjs | null;
    onDateChange?: (date: dayjs.Dayjs | null) => void;
    sx?: SxProps;
    disableElevation?: boolean;
}

const CustomDatePicker: React.FC<Props> = (props) => {
    const {date, onDateChange} = props;
    const [isOpen, setIsOpen] = useState(false);

    const onChange = (date: dayjs.Dayjs | null) => {
        setIsOpen(false);
        if (onDateChange) {
            onDateChange(date);
        }
    };

    return (
        <DatePicker
            open={isOpen}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            value={date ? date : null}
            onChange={(newValue) => {
                onChange(newValue ? newValue : null);
            }}
            sx={{minWidth: "160px"}}
            slotProps={{
                textField: {
                    variant: "filled",
                    fullWidth: true,
                    InputProps: {
                        disableUnderline: true,
                        fullWidth: true,
                        endAdornment: (
                            <IconButton onClick={() => setIsOpen(!isOpen)}>
                                {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        ),
                    },
                },
            }}
        />
    );
};
export const CustomDateRangeFilter = (prop: {
    column: MRT_Column<Order, unknown>;
    t: TFunction<"translation", undefined>;
}) => {
    const setFilter = prop.column.setFilterValue;
    const t = prop.t;
    const [rangeType, setRangeType] = useState("");
    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    const [endDate, setEndDate] = useState<Dayjs | null>(null);
    const [customStartDate, setCustomStartDate] = useState<Dayjs>(dayjs().subtract(30, "day"));
    const [customEndDate, setCustomEndDate] = useState(dayjs());

    const handleRangeChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        setRangeType(value);

        const today = dayjs();

        switch (value) {
            case "5days":
                setStartDate(today.subtract(5, "day"));
                setEndDate(today);
                break;
            case "10days":
                setStartDate(today.subtract(10, "day"));
                setEndDate(today);
                break;
            case "30days":
                setStartDate(today.subtract(30, "day"));
                setEndDate(today);
                break;
            case "custom":
                setStartDate(customStartDate);
                setEndDate(customEndDate);
                break;
            default:
                setStartDate(null);
                setEndDate(null);
        }
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (startDate && endDate) {
            setFilter([startDate, endDate]);
        } else {
            setFilter("");
        }
    }, [startDate, endDate]);
    const handleStartDateChange = (date: Dayjs | null) => {
        const selectedDate = dayjs(date);
        setStartDate(selectedDate);
        setCustomStartDate(selectedDate);
    };
    const handleEndDateChange = (date: Dayjs | null) => {
        const selectedDate = dayjs(date);
        setEndDate(selectedDate);
        setCustomEndDate(selectedDate);
    };

    return (
        <>
            <Select
                value={rangeType}
                onChange={handleRangeChange}
                autoWidth={true}
                variant="filled"
                disableUnderline={true}
                fullWidth={true}
                inputProps={{
                    size: "small",
                    sx: {
                        fontSize: "14px",
                    },
                }}
                sx={{
                    marginTop: "0.25rem",
                }}
            >
                <MenuItem value="All">{t("enumerations.all")}</MenuItem>

                <MenuItem value="5days">{t("admin.order_list_view.filters.days", {days: 5})}</MenuItem>
                <MenuItem value="10days">{t("admin.order_list_view.filters.days", {days: 10})}</MenuItem>
                <MenuItem value="30days">{t("admin.order_list_view.filters.days", {days: 30})}</MenuItem>
                <MenuItem value="custom">{t("admin.order_list_view.filters.custom")}</MenuItem>
            </Select>

            {rangeType === "custom" && (
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                        backgroundColor: "#E9EBF1",
                    }}
                >
                    <CustomDatePicker
                        date={customStartDate}
                        onDateChange={(date) => {
                            if (date && !date.isBefore(customEndDate)) {
                                setCustomEndDate(date.add(1, "day"));
                            }
                            handleStartDateChange(date);
                        }}
                        sx={{width: "100%"}}
                    />
                    <Typography variant="body1" sx={{mx: 1}}>
                        –
                    </Typography>
                    <CustomDatePicker
                        date={customEndDate}
                        onDateChange={(date) => handleEndDateChange(date)}
                        sx={{width: "100%"}}
                    />
                </Stack>
            )}
        </>
    );
};
